/**
 * World validation.
 *
 * The kingdom map is data, so a mistyped concept id is a runtime bug rather
 * than a compile error — and the failure mode is quiet: a region silently one
 * concept short, or a concept that exists in the curriculum and nowhere on the
 * map, so a student finishes a kingdom and is never credited for it. This runs
 * in `npm run content:validate` and in the suite, alongside
 * `validateCurriculum`, and reuses its `ValidationIssue` shape so the CLI
 * prints both lists the same way.
 */

import type { CurriculumRegistry, ValidationIssue } from '@yuan-math/curriculum';
import type { ExerciseBank } from '@yuan-math/exercises';
import {
  bossClaimId,
  BOSS_GOLD_MAX,
  BOSS_GOLD_MIN,
  BOSS_XP_MAX,
  BOSS_XP_MIN,
  type BossDef,
} from './boss.ts';
import {
  ART_SLOT,
  ITEM_SLOTS,
  type ItemCondition,
  type ItemDef,
  type ItemSlot,
  type TitleDef,
} from './items.ts';
import { SITE_PREFIX } from './sites.ts';
import { QUEST_CORRECT_LIMIT, type QuestDef } from './quests.ts';
import { kingdomConceptIds } from './state.ts';
import type { Kingdom } from './types.ts';

export function validateWorld(kingdoms: readonly Kingdom[], registry: CurriculumRegistry): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const error = (code: string, message: string, subject?: string): void => {
    issues.push({ severity: 'error', code, message, subject });
  };
  const warn = (code: string, message: string, subject?: string): void => {
    issues.push({ severity: 'warning', code, message, subject });
  };

  assertUnique(kingdoms.map((k) => k.id), 'kingdom id', error);
  assertUnique(kingdoms.flatMap((k) => k.regions.map((r) => r.id)), 'region id', error);

  // Order is what makes the seals acyclic — an unlock may only look backwards —
  // so two kingdoms sharing a rung leaves "backwards" undefined between them.
  assertUnique(kingdoms.map((k) => String(k.order)), 'kingdom order', error);

  const strandIds = new Set(registry.strands.map((s) => s.id));
  const bossOwner = new Map<string, Kingdom>();
  for (const kingdom of kingdoms) {
    for (const strandId of kingdom.strandIds) {
      if (!strandIds.has(strandId)) {
        error('unknown-strand', `Kingdom "${kingdom.id}" references unknown strand "${strandId}"`, kingdom.id);
      }
    }
    if (kingdom.bossId) {
      const owner = bossOwner.get(kingdom.bossId);
      if (owner) {
        error(
          'duplicate-boss',
          `Kingdoms "${owner.id}" and "${kingdom.id}" both claim boss "${kingdom.bossId}"`,
          kingdom.id,
        );
      }
      bossOwner.set(kingdom.bossId, kingdom);
    }
  }

  /* Where each concept sits, and the first duplicate placement of it. */
  const kingdomOfConcept = new Map<string, Kingdom>();
  const regionOfConcept = new Map<string, string>();

  for (const kingdom of kingdoms) {
    for (const region of kingdom.regions) {
      if (region.conceptIds.length === 0) {
        warn('empty-region', `Region "${region.id}" holds no concepts`, region.id);
      }
      for (const conceptId of region.conceptIds) {
        if (!registry.concept(conceptId)) {
          error('unknown-concept', `Region "${region.id}" references unknown concept "${conceptId}"`, region.id);
          continue;
        }
        const seen = regionOfConcept.get(conceptId);
        if (seen) {
          error(
            'concept-in-two-regions',
            `Concept "${conceptId}" appears in regions "${seen}" and "${region.id}"`,
            conceptId,
          );
          continue;
        }
        regionOfConcept.set(conceptId, region.id);
        kingdomOfConcept.set(conceptId, kingdom);
      }
    }
  }

  // A teachable concept nobody put on the map is invisible to the world: it
  // counts towards no kingdom, so finishing it moves no seal.
  for (const concept of registry.availableConcepts()) {
    if (!kingdomOfConcept.has(concept.id)) {
      error('unplaced-concept', `Concept "${concept.id}" is available but belongs to no region`, concept.id);
    }
  }

  for (const kingdom of kingdoms) {
    for (const [index, clause] of kingdom.unlock.known.entries()) {
      const where = `${kingdom.id} unlock clause ${index}`;
      if (!(clause.share > 0) || clause.share > 1) {
        error('invalid-share', `${where} has share ${clause.share}, which is not in (0, 1]`, kingdom.id);
      }
      if (clause.conceptIds.length === 0) {
        warn('empty-clause', `${where} names no concepts, so it is satisfied by doing nothing`, kingdom.id);
      }
      for (const conceptId of clause.conceptIds) {
        const owner = kingdomOfConcept.get(conceptId);
        if (!owner) {
          error(
            'unlock-unknown-concept',
            `${where} requires "${conceptId}", which belongs to no region`,
            kingdom.id,
          );
          continue;
        }
        // Requiring a concept from this kingdom or a later one is a seal no
        // student can open by travelling forwards — the loop `order` exists to
        // rule out.
        if (owner.order >= kingdom.order) {
          error(
            'forward-unlock',
            `${where} requires "${conceptId}", which belongs to "${owner.id}" (order ${owner.order}, not below ${kingdom.order})`,
            kingdom.id,
          );
        }
      }
    }

    for (const bossId of kingdom.unlock.clearedBossIds) {
      const owner = bossOwner.get(bossId);
      if (!owner) {
        error('unknown-boss', `Kingdom "${kingdom.id}" requires unknown boss "${bossId}"`, kingdom.id);
        continue;
      }
      if (owner.order >= kingdom.order) {
        error(
          'forward-unlock',
          `Kingdom "${kingdom.id}" requires boss "${bossId}" of "${owner.id}" (order ${owner.order}, not below ${kingdom.order})`,
          kingdom.id,
        );
      }
    }
  }

  return issues;
}

/* -------------------------------------------------------------------------- */
/* Quests                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * The most a single quest may pay.
 *
 * `XP_LESSON_COMPLETE` is 20, so these are ten and two and a half lessons'
 * worth. Quest XP flows into the same `levelForXp` economy as everything else
 * (§0.2) and a quest paying a hundred lessons' worth would make the level a
 * measure of how much story a student had read. Bounded rather than fixed
 * because a chain's finale should feel like one.
 */
export const QUEST_XP_LIMIT = 200;
export const QUEST_GOLD_LIMIT = 50;

/**
 * Quest validation.
 *
 * Quests are data pointing at other data — concepts, lessons, kingdoms, each
 * other — and every one of those pointers fails quietly. A mistyped lesson id
 * is an objective that can never be met; a main chain with a gap in it is a
 * kingdom whose story stops halfway with nothing on screen to say why; a quest
 * naming another kingdom's quest is a lock a student cannot see the key to.
 * None of that throws, and none of it looks wrong in the source.
 *
 * The bank is a parameter for the same reason it is one on `validateBosses`:
 * an objective is only reachable if the mathematics it names can actually be
 * *done*. "Master Complex Numbers" over a `planned` concept, or over one with
 * no exercise to attempt, is a quest that sits at 0 of 1 for ever — and on
 * screen that is indistinguishable from a quest the student simply has not got
 * to yet, which is the quietest failure this file has to catch. Regions are
 * allowed to name planned concepts, to show where the mathematics leads
 * (`types.ts`), so nothing upstream rules this out.
 */
export function validateQuests(
  quests: readonly QuestDef[],
  kingdoms: readonly Kingdom[],
  registry: CurriculumRegistry,
  bank: ExerciseBank,
): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const error = (code: string, message: string, subject?: string): void => {
    issues.push({ severity: 'error', code, message, subject });
  };
  const warn = (code: string, message: string, subject?: string): void => {
    issues.push({ severity: 'warning', code, message, subject });
  };

  assertUnique(quests.map((quest) => quest.id), 'quest id', error);

  const questById = new Map(quests.map((quest) => [quest.id, quest]));
  const kingdomById = new Map(kingdoms.map((kingdom) => [kingdom.id, kingdom]));
  const kingdomOfConcept = new Map<string, string>();
  for (const kingdom of kingdoms) {
    for (const conceptId of kingdomConceptIds(kingdom)) kingdomOfConcept.set(conceptId, kingdom.id);
  }

  for (const quest of quests) {
    // Quest, boss and site rewards are all settled out of the same
    // `claimedQuestIds` list, and these prefixes are what keep them apart. A
    // quest wearing one could be paid by a win it has nothing to do with, or
    // silently block one.
    for (const [prefix, what] of RESERVED_CLAIM_PREFIXES) {
      if (!quest.id.startsWith(prefix)) continue;
      error('reserved-id', `Quest "${quest.id}" starts with "${prefix}", which is reserved for ${what}`, quest.id);
    }

    if (!kingdomById.has(quest.kingdomId)) {
      // An orphan: on no board, reachable from nothing, rewarding a story that
      // is not told anywhere.
      error('unknown-kingdom', `Quest "${quest.id}" belongs to unknown kingdom "${quest.kingdomId}"`, quest.id);
    }

    if (quest.title.trim().length === 0) {
      error('empty-title', `Quest "${quest.id}" has no title`, quest.id);
    }
    if (quest.story.length === 0) {
      error('empty-story', `Quest "${quest.id}" tells no story, which is the whole of what a quest adds`, quest.id);
    }
    for (const beat of quest.story) {
      // §14: a problem the world has, in two to four short paragraphs. A wall
      // of text is a wall of text however good the wall is.
      if (beat.text.length > STORY_BEAT_LIMIT) {
        warn(
          'long-story',
          `Quest "${quest.id}" has a story paragraph of ${beat.text.length} characters (over ${STORY_BEAT_LIMIT}); §14 asks for short ones`,
          quest.id,
        );
      }
    }

    validateReward(quest, error);
    validateObjective(quest, questById, kingdomOfConcept, registry, bank, error);
  }

  validateChains(quests, error);

  return issues;
}

/** Long enough for a paragraph of story, short enough not to be a page of it. */
const STORY_BEAT_LIMIT = 320;

function validateReward(quest: QuestDef, error: Reporter): void {
  const { xp, gold } = quest.reward;
  for (const [name, value, limit] of [
    ['xp', xp, QUEST_XP_LIMIT],
    ['gold', gold, QUEST_GOLD_LIMIT],
  ] as const) {
    if (!Number.isInteger(value) || value <= 0) {
      // Zero is not a reward, and a negative one takes XP off a student for
      // finishing something — the opposite of what §0.6 asks of every screen.
      error('invalid-reward', `Quest "${quest.id}" pays ${value} ${name}; a reward must be a positive whole number`, quest.id);
    } else if (value > limit) {
      error('invalid-reward', `Quest "${quest.id}" pays ${value} ${name}, over the ${limit} cap`, quest.id);
    }
  }
}

function validateObjective(
  quest: QuestDef,
  questById: ReadonlyMap<string, QuestDef>,
  kingdomOfConcept: ReadonlyMap<string, string>,
  registry: CurriculumRegistry,
  bank: ExerciseBank,
  error: Reporter,
): void {
  const objective = quest.objective;

  /**
   * A concept a quest names must exist, be in this kingdom, and be something a
   * student can actually attempt — both `master-concepts` and `practice` are
   * counted off the attempt log, so a concept with nothing to attempt is an
   * objective with no path to completion.
   */
  const checkConcepts = (conceptIds: string[]): void => {
    if (conceptIds.length === 0) {
      error('empty-objective', `Quest "${quest.id}" names no concepts, so its objective asks for nothing`, quest.id);
    }
    for (const conceptId of conceptIds) {
      const concept = registry.concept(conceptId);
      if (!concept) {
        error('unknown-concept', `Quest "${quest.id}" references unknown concept "${conceptId}"`, quest.id);
        continue;
      }
      if (concept.status !== 'available') {
        error(
          'unteachable-concept',
          `Quest "${quest.id}" requires "${conceptId}", which is ${concept.status}: there is nothing to attempt, so the objective can never be met`,
          quest.id,
        );
      } else if (!bank.hasContent(conceptId)) {
        error(
          'no-exercises',
          `Quest "${quest.id}" requires "${conceptId}", which has no exercises — mastery and practice are both counted from attempts, and none can be made`,
          quest.id,
        );
      }
      const owner = kingdomOfConcept.get(conceptId);
      if (owner !== quest.kingdomId) {
        // A quest is a kingdom's story and its mathematics is that kingdom's
        // mathematics. One sending a student somewhere else belongs on the
        // board of the kingdom it sends them to.
        error(
          'foreign-concept',
          `Quest "${quest.id}" of "${quest.kingdomId}" requires "${conceptId}", which belongs to ${owner ? `"${owner}"` : 'no kingdom'}`,
          quest.id,
        );
      }
    }
  };

  switch (objective.kind) {
    case 'master-concepts': {
      checkConcepts(objective.conceptIds);
      if (!Number.isInteger(objective.count) || objective.count < 1 || objective.count > objective.conceptIds.length) {
        error(
          'invalid-count',
          `Quest "${quest.id}" asks for ${objective.count} of ${objective.conceptIds.length} concepts; a count no student could reach is a quest that never ends`,
          quest.id,
        );
      }
      break;
    }

    case 'practice': {
      checkConcepts(objective.conceptIds);
      if (!Number.isInteger(objective.correctCount) || objective.correctCount < 1) {
        error('invalid-count', `Quest "${quest.id}" asks for ${objective.correctCount} correct answers`, quest.id);
      } else if (objective.correctCount > QUEST_CORRECT_LIMIT) {
        error(
          'invalid-count',
          `Quest "${quest.id}" asks for ${objective.correctCount} correct answers, over the ${QUEST_CORRECT_LIMIT} cap: practice is counted from the attempt log, which is bounded at ATTEMPT_LOG_LIMIT, so a long objective can un-finish itself as the student's own early answers roll out of the window`,
          quest.id,
        );
      }
      break;
    }

    case 'complete-lessons': {
      if (objective.lessonIds.length === 0) {
        error('empty-objective', `Quest "${quest.id}" names no lessons, so its objective asks for nothing`, quest.id);
      }
      for (const lessonId of objective.lessonIds) {
        const lesson = registry.lesson(lessonId);
        if (!lesson) {
          error('unknown-lesson', `Quest "${quest.id}" references unknown lesson "${lessonId}"`, quest.id);
          continue;
        }
        const owner = kingdomOfConcept.get(lesson.conceptId);
        if (owner !== quest.kingdomId) {
          error(
            'foreign-lesson',
            `Quest "${quest.id}" of "${quest.kingdomId}" requires lesson "${lessonId}", whose concept belongs to ${owner ? `"${owner}"` : 'no kingdom'}`,
            quest.id,
          );
        }
      }
      break;
    }

    case 'clear-quests': {
      if (objective.questIds.length === 0) {
        error('empty-objective', `Quest "${quest.id}" names no quests, so its objective asks for nothing`, quest.id);
      }
      for (const questId of objective.questIds) {
        const other = questById.get(questId);
        if (!other) {
          error('unknown-quest', `Quest "${quest.id}" requires unknown quest "${questId}"`, quest.id);
          continue;
        }
        if (other.kingdomId !== quest.kingdomId) {
          error(
            'foreign-quest',
            `Quest "${quest.id}" of "${quest.kingdomId}" requires quest "${questId}" of "${other.kingdomId}"`,
            quest.id,
          );
          continue;
        }
        if (other.order >= quest.order) {
          // Strictly earlier, which is also what rules out a quest requiring
          // itself and any ring of quests requiring each other for ever.
          error(
            'forward-quest',
            `Quest "${quest.id}" (order ${quest.order}) requires "${questId}" (order ${other.order}), which is not earlier on the board`,
            quest.id,
          );
        }
      }
      break;
    }
  }
}

/**
 * The main chain of each kingdom: 0, 1, 2, … with no gaps and no ties.
 *
 * A gap is not cosmetic. `questProgress` locks a main quest behind *every*
 * earlier main quest of its kingdom, so the numbering is the chain — and two
 * quests sharing a rung leaves "earlier" undefined between them, the same
 * reason `validateWorld` insists on distinct kingdom orders.
 */
function validateChains(quests: readonly QuestDef[], error: Reporter): void {
  const byKingdom = new Map<string, QuestDef[]>();
  for (const quest of quests) {
    byKingdom.set(quest.kingdomId, [...(byKingdom.get(quest.kingdomId) ?? []), quest]);
  }

  for (const [kingdomId, board] of byKingdom) {
    const orders = board.map((quest) => quest.order);
    if (new Set(orders).size !== orders.length) {
      error('duplicate-order', `Kingdom "${kingdomId}" has two quests on the same board position`, kingdomId);
    }

    const chain = board.filter((quest) => quest.kind === 'main').map((quest) => quest.order).sort((a, b) => a - b);
    const expected = chain.map((_, index) => index);
    if (chain.join() !== expected.join()) {
      error(
        'chain-gap',
        `Kingdom "${kingdomId}" has a main chain of orders [${chain.join(', ')}]; it must run 0…${chain.length - 1} with no gaps`,
        kingdomId,
      );
    }
  }
}

/* -------------------------------------------------------------------------- */
/* Bosses                                                                     */
/* -------------------------------------------------------------------------- */

/** The namespace boss reward claims live in, derived from the id builder itself. */
const BOSS_CLAIM_PREFIX = bossClaimId('');

/** Namespaces inside `claimedQuestIds` that a quest id may not stray into. */
const RESERVED_CLAIM_PREFIXES: ReadonlyArray<readonly [string, string]> = [
  [BOSS_CLAIM_PREFIX, 'boss reward claims'],
  [SITE_PREFIX, 'warded site reward claims'],
];

/** §15: one to three. A four-stage boss is the "20 random questions" it forbids. */
const MAX_STAGES = 3;

/**
 * The size of a stage.
 *
 * Three is the floor because the pass threshold has to mean something: at two
 * questions, 75% rounds up to both, so one slip is a defeat. Five is the
 * ceiling because a fifteen-question boss stops being a culmination and starts
 * being a test.
 */
const MIN_QUESTIONS = 3;
const MAX_QUESTIONS = 5;

/**
 * Boss validation.
 *
 * A boss points at more things than a quest does and every pointer fails the
 * same quiet way. A stage naming a concept with no exercise template is a
 * fight that throws when a student opens it; a stage naming another kingdom's
 * concept is a dragon asking about mathematics nobody in that kingdom was
 * taught; a kingdom whose `bossId` names nothing is a seal with no key, because
 * `worldState` will only ever call it `cleared` once that boss is beaten and
 * nothing can beat a boss that does not exist.
 *
 * The bank is a parameter for the same reason the registry is: this package
 * sits below `content/` and cannot import either.
 */
export function validateBosses(
  bosses: readonly BossDef[],
  kingdoms: readonly Kingdom[],
  registry: CurriculumRegistry,
  bank: ExerciseBank,
): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const error = (code: string, message: string, subject?: string): void => {
    issues.push({ severity: 'error', code, message, subject });
  };
  const warn = (code: string, message: string, subject?: string): void => {
    issues.push({ severity: 'warning', code, message, subject });
  };

  assertUnique(bosses.map((boss) => boss.id), 'boss id', error);

  const bossById = new Map(bosses.map((boss) => [boss.id, boss]));
  const kingdomById = new Map(kingdoms.map((kingdom) => [kingdom.id, kingdom]));
  const kingdomOfConcept = new Map<string, string>();
  for (const kingdom of kingdoms) {
    for (const conceptId of kingdomConceptIds(kingdom)) kingdomOfConcept.set(conceptId, kingdom.id);
  }

  // Both directions. A kingdom naming a boss nobody defined can never be
  // cleared; a boss no kingdom names is a fight with no gate to reach it by.
  for (const kingdom of kingdoms) {
    if (kingdom.bossId && !bossById.has(kingdom.bossId)) {
      error('unknown-boss', `Kingdom "${kingdom.id}" names boss "${kingdom.bossId}", which is not defined`, kingdom.id);
    }
  }

  for (const boss of bosses) {
    const kingdom = kingdomById.get(boss.kingdomId);
    if (!kingdom) {
      error('unknown-kingdom', `Boss "${boss.id}" belongs to unknown kingdom "${boss.kingdomId}"`, boss.id);
    } else if (kingdom.bossId !== boss.id) {
      error(
        'unclaimed-boss',
        `Boss "${boss.id}" says it belongs to "${kingdom.id}", but that kingdom's bossId is ${kingdom.bossId ? `"${kingdom.bossId}"` : 'unset'}`,
        boss.id,
      );
    }

    if (boss.name.trim().length === 0) error('empty-name', `Boss "${boss.id}" has no name`, boss.id);
    if (boss.emoji.trim().length === 0) error('empty-emoji', `Boss "${boss.id}" has no emoji`, boss.id);

    validateBossStory(boss, error, warn);
    validateBossReward(boss, error);
    validateBossStages(boss, registry, bank, kingdomOfConcept, error);
  }

  return issues;
}

function validateBossStory(boss: BossDef, error: Reporter, warn: Reporter): void {
  for (const part of ['intro', 'victory', 'defeat'] as const) {
    const lines = boss.story[part];
    if (lines.length === 0) {
      // `defeat` especially: a lost battle whose screen has nothing to say is
      // the punishment §12 spends a section asking this platform not to build.
      error('empty-story', `Boss "${boss.id}" has no ${part} lines`, boss.id);
    }
    for (const line of lines) {
      if (line.trim().length === 0) {
        error('empty-story', `Boss "${boss.id}" has a blank ${part} line`, boss.id);
      } else if (line.length > STORY_BEAT_LIMIT) {
        warn(
          'long-story',
          `Boss "${boss.id}" has a ${part} line of ${line.length} characters (over ${STORY_BEAT_LIMIT}); §15 asks for a fight, not a monologue`,
          boss.id,
        );
      }
    }
  }
}

function validateBossReward(boss: BossDef, error: Reporter): void {
  for (const [name, value, min, max] of [
    ['xp', boss.reward.xp, BOSS_XP_MIN, BOSS_XP_MAX],
    ['gold', boss.reward.gold, BOSS_GOLD_MIN, BOSS_GOLD_MAX],
  ] as const) {
    if (!Number.isInteger(value) || value < min || value > max) {
      error(
        'invalid-reward',
        `Boss "${boss.id}" pays ${value} ${name}; a boss reward is a whole number from ${min} to ${max}`,
        boss.id,
      );
    }
  }
}

function validateBossStages(
  boss: BossDef,
  registry: CurriculumRegistry,
  bank: ExerciseBank,
  kingdomOfConcept: ReadonlyMap<string, string>,
  error: Reporter,
): void {
  if (boss.stages.length === 0 || boss.stages.length > MAX_STAGES) {
    error(
      'invalid-stages',
      `Boss "${boss.id}" has ${boss.stages.length} stages; §15 asks for 1 to ${MAX_STAGES}`,
      boss.id,
    );
  }

  let previousDifficulty = 0;
  for (const [index, stage] of boss.stages.entries()) {
    const where = `Boss "${boss.id}" stage ${index} ("${stage.name}")`;

    if (stage.name.trim().length === 0) {
      error('empty-name', `Boss "${boss.id}" stage ${index} has no name`, boss.id);
    }

    if (!Number.isInteger(stage.difficulty) || stage.difficulty < 1 || stage.difficulty > 5) {
      error('invalid-difficulty', `${where} is difficulty ${stage.difficulty}, which is not 1–5`, boss.id);
    } else if (stage.difficulty < previousDifficulty) {
      // §15: a culmination, climbing. A boss that gets easier as it goes tells
      // the student the stages were in no particular order after all.
      error(
        'falling-difficulty',
        `${where} is difficulty ${stage.difficulty}, below stage ${index - 1}'s ${previousDifficulty}`,
        boss.id,
      );
    }
    previousDifficulty = Math.max(previousDifficulty, stage.difficulty);

    if (
      !Number.isInteger(stage.questionCount) ||
      stage.questionCount < MIN_QUESTIONS ||
      stage.questionCount > MAX_QUESTIONS
    ) {
      error(
        'invalid-question-count',
        `${where} asks ${stage.questionCount} questions; a stage is ${MIN_QUESTIONS}–${MAX_QUESTIONS}`,
        boss.id,
      );
    }

    if (stage.conceptIds.length === 0) {
      error('empty-stage', `${where} names no concepts, so there is nothing to ask`, boss.id);
    }

    for (const conceptId of stage.conceptIds) {
      const concept = registry.concept(conceptId);
      if (!concept) {
        error('unknown-concept', `${where} references unknown concept "${conceptId}"`, boss.id);
        continue;
      }
      if (concept.status !== 'available') {
        // A planned concept has no lessons and no templates: the stage would
        // throw the moment a student opened the fight.
        error('unteachable-concept', `${where} requires "${conceptId}", which is ${concept.status}`, boss.id);
      }
      const owner = kingdomOfConcept.get(conceptId);
      if (owner !== boss.kingdomId) {
        error(
          'foreign-concept',
          `${where} requires "${conceptId}", which belongs to ${owner ? `"${owner}"` : 'no kingdom'} rather than "${boss.kingdomId}"`,
          boss.id,
        );
      }
      const templates = bank.templatesFor(conceptId);
      if (templates.length === 0) {
        /*
         * Templates specifically, not `hasContent`. A boss generates a fresh
         * problem per question from a seed; a concept with only authored
         * exercises would hand back the same one every time, so a retry would
         * be the same fight and the dedupe pass would run out of alternatives.
         */
        error(
          'no-templates',
          `${where} requires "${conceptId}", which has no exercise templates to generate a question from`,
          boss.id,
        );
      } else if (!templates.some((template) => template.difficulties.includes(stage.difficulty))) {
        /*
         * The stage's difficulty is a promise about how hard the fight gets,
         * and `bank.single` keeps it quietly: asked for a band no template
         * offers, it substitutes the nearest one that exists. So a stage marked
         * 5 over concepts that stop at 3 is not a harder stage — it is the same
         * stage with a bigger number on the banner, and the climb §15 asks for
         * is nominal for exactly as long as nobody checks.
         */
        error(
          'unsupported-difficulty',
          `${where} is difficulty ${stage.difficulty}, which no template of "${conceptId}" offers (it has ${[
            ...new Set(templates.flatMap((template) => template.difficulties)),
          ]
            .sort()
            .join(', ')}); the question would be generated at the nearest band instead`,
          boss.id,
        );
      }
    }
  }
}

/* -------------------------------------------------------------------------- */
/* Items and titles                                                            */
/* -------------------------------------------------------------------------- */

/**
 * The gold an item may be priced at.
 *
 * A main quest pays 10–30 gold and a boss 40–80, so the cheapest thing in the
 * shop is about one quest and the dearest is about two bosses. Both ends are
 * enforced. A floor because a cosmetic worth three gold makes the currency
 * meaningless; a ceiling because a price a student cannot reach in a term is
 * artificial scarcity, which is the fourth item on §44's list — and because the
 * only way to raise gold faster is to do more mathematics, which is exactly the
 * grind §47 asks the platform not to reward.
 */
export const ITEM_GOLD_MIN = 30;
export const ITEM_GOLD_MAX = 120;

/**
 * Item and title validation.
 *
 * Same failure mode as everywhere else in this file, and quieter than most: an
 * item whose condition names a boss nobody defined is one no student can ever
 * earn, and it looks exactly like an item they have not earned *yet*. A tile
 * reading "Defeat boss-calulus-kingdom" is the good case; the bad case is a
 * strand condition asking for more concepts than the strand teaches, which
 * reads as a perfectly ordinary "0 of 40" forever.
 *
 * The lists arrive as parameters, like every other definition this package
 * validates: they live in `content/`, which sits above it.
 */
export function validateItems(
  items: readonly ItemDef[],
  titles: readonly TitleDef[],
  bosses: readonly BossDef[],
  registry: CurriculumRegistry,
): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const error = (code: string, message: string, subject?: string): void => {
    issues.push({ severity: 'error', code, message, subject });
  };
  const warn = (code: string, message: string, subject?: string): void => {
    issues.push({ severity: 'warning', code, message, subject });
  };

  assertUnique(items.map((item) => item.id), 'item id', error);
  assertUnique(titles.map((title) => title.id), 'title id', error);
  // Two items sharing a drawing are two names for one thing on the avatar, and
  // a student who owns both can never tell which one they are wearing.
  assertUnique(items.map((item) => item.art), 'item art', error);

  const slots = new Set<string>(ITEM_SLOTS);
  const bossIds = new Set(bosses.map((boss) => boss.id));
  const strandIds = new Set<string>(registry.strands.map((strand) => strand.id));

  /** Available concepts per strand — the ceiling a strand condition may ask for. */
  const teachablePerStrand = new Map<string, number>();
  for (const concept of registry.availableConcepts()) {
    teachablePerStrand.set(concept.strand, (teachablePerStrand.get(concept.strand) ?? 0) + 1);
  }

  const checkCondition = (condition: ItemCondition, subject: string, what: string): void => {
    switch (condition.kind) {
      case 'master-concepts-in-strand': {
        if (!strandIds.has(condition.strandId)) {
          error('unknown-strand', `${what} requires unknown strand "${condition.strandId}"`, subject);
          return;
        }
        if (!Number.isInteger(condition.count) || condition.count < 1) {
          error(
            'invalid-condition',
            `${what} asks for ${condition.count} mastered concepts, which is not a positive whole number`,
            subject,
          );
          return;
        }
        const teachable = teachablePerStrand.get(condition.strandId) ?? 0;
        if (condition.count > teachable) {
          // Unearnable, and indistinguishable on screen from not-yet-earned.
          error(
            'unreachable-condition',
            `${what} asks for ${condition.count} mastered "${condition.strandId}" concepts, but only ${teachable} are teachable today`,
            subject,
          );
        }
        break;
      }
      case 'clear-boss':
        if (!bossIds.has(condition.bossId)) {
          error('unknown-boss', `${what} requires clearing boss "${condition.bossId}", which is not defined`, subject);
        }
        break;
      case 'claim-quests':
        if (!Number.isInteger(condition.count) || condition.count < 1) {
          error(
            'invalid-condition',
            `${what} asks for ${condition.count} claimed quests, which is not a positive whole number`,
            subject,
          );
        }
        break;
    }
  };

  for (const item of items) {
    if (item.name.trim().length === 0) error('empty-name', `Item "${item.id}" has no name`, item.id);
    if (item.emoji.trim().length === 0) error('empty-emoji', `Item "${item.id}" has no emoji`, item.id);
    if (item.flavor.trim().length === 0) {
      // The line under the name is the whole of what makes a cosmetic worth
      // wanting; without it the tile is an emoji and a price.
      warn('empty-flavor', `Item "${item.id}" has no flavour line`, item.id);
    }

    if (!slots.has(item.slot)) {
      error('invalid-slot', `Item "${item.id}" is worn in unknown slot "${item.slot}"`, item.id);
    } else {
      const drawnFor: ItemSlot | undefined = ART_SLOT[item.art];
      if (drawnFor === undefined) {
        error('unknown-art', `Item "${item.id}" names unknown art "${item.art}"`, item.id);
      } else if (drawnFor !== item.slot) {
        // A cape drawn in the hands. The layer would render in the wrong place
        // and nothing else in the codebase would notice.
        error(
          'art-slot-mismatch',
          `Item "${item.id}" is worn as "${item.slot}" but its art is drawn for "${drawnFor}"`,
          item.id,
        );
      }
    }

    if (item.source.kind === 'shop') {
      const { gold } = item.source;
      if (!Number.isInteger(gold) || gold <= 0) {
        error('invalid-price', `Item "${item.id}" costs ${gold} gold; a price must be a positive whole number`, item.id);
      } else if (gold < ITEM_GOLD_MIN || gold > ITEM_GOLD_MAX) {
        error('invalid-price', `Item "${item.id}" costs ${gold} gold, outside the ${ITEM_GOLD_MIN}–${ITEM_GOLD_MAX} range`, item.id);
      }
    } else {
      checkCondition(item.source.condition, item.id, `Item "${item.id}"`);
    }
  }

  for (const title of titles) {
    if (title.text.trim().length === 0) error('empty-title', `Title "${title.id}" has no text`, title.id);
    checkCondition(title.condition, title.id, `Title "${title.id}"`);
  }

  return issues;
}

type Reporter = (code: string, message: string, subject?: string) => void;

/** Reports every repeated id, in the style of `validateCurriculum`. */
function assertUnique(
  ids: string[],
  kind: string,
  error: (code: string, message: string, subject?: string) => void,
): void {
  const seen = new Set<string>();
  for (const id of ids) {
    if (seen.has(id)) error('duplicate-id', `Duplicate ${kind}: "${id}"`, id);
    seen.add(id);
  }
}
